/**
 * The shape of the in-app tutorial. The text lives in one file per language
 * (content.de.ts, content.en.ts); a test checks both cover every tool.
 */

export type Language = 'de' | 'en'

export type ToolId =
    | 'breaking'
    | 'morning_briefing'
    | 'newsflash'
    | 'generator'
    | 'headlines'
    | 'crypto_headlines'
    | 'single_headline'
    | 'revenue'
    | 'formalia'
    | 'seo'
    | 'infographics'
    | 'find_similar'

export type StartPageId = 'welcome' | 'choose' | 'workflow'
export type ReferencePageId = 'glossary' | 'troubleshooting'
export type PageId = StartPageId | ToolId | ReferencePageId

/** The app views a tool page can open (the sidebar entries in App.tsx). */
export type ToolView =
    | 'breaking-news'
    | 'morning-briefing'
    | 'newsflash'
    | 'article-generator'
    | 'headlines-generator'
    | 'crypto-headlines-generator'
    | 'single-headline'
    | 'revenue-generator'
    | 'formalia-check'
    | 'seo-tool'
    | 'infographics'
    | 'find-similar'

export const TOOL_VIEW: Record<ToolId, ToolView> = {
    breaking: 'breaking-news',
    morning_briefing: 'morning-briefing',
    newsflash: 'newsflash',
    generator: 'article-generator',
    headlines: 'headlines-generator',
    crypto_headlines: 'crypto-headlines-generator',
    single_headline: 'single-headline',
    revenue: 'revenue-generator',
    formalia: 'formalia-check',
    seo: 'seo-tool',
    infographics: 'infographics',
    find_similar: 'find-similar',
}

export interface Step {
    title: string
    desc: string
}

export interface Term {
    name: string
    desc: string
}

export interface QA {
    q: string
    a: string
}

export interface ToolGuide {
    /** As in the app's sidebar. */
    title: string
    /** One sentence: what the tool does. */
    tagline: string
    badge: string
    /** Realistic duration of one run. */
    time: string
    whenToUse: string[]
    whatYouGet: string[]
    steps: Step[]
    /** Every field and option on the tool's page. */
    options: Term[]
    /** How it works: sources, checks, models — for the curious. */
    behindTheScenes: string[]
    tips: string[]
    troubleshooting: QA[]
    related: ToolId[]
}

export interface TutorialContent {
    ui: {
        title: string
        subtitle: string
        searchPlaceholder: string
        noResults: string
        openTool: string
        time: string
        whenToUse: string
        whatYouGet: string
        steps: string
        options: string
        behindTheScenes: string
        tips: string
        troubleshooting: string
        related: string
        next: string
        glossaryFilter: string
    }
    nav: {
        start: string
        groups: { label: string; tools: ToolId[] }[]
        reference: string
        pages: Record<StartPageId | ReferencePageId, string>
    }
    welcome: {
        title: string
        intro: string
        rulesTitle: string
        rules: Term[]
        quickStartTitle: string
        quickStart: string[]
        highlightsTitle: string
        highlights: { tool: ToolId; text: string }[]
    }
    choose: {
        title: string
        intro: string
        rows: { need: string; tool: ToolId; why: string }[]
    }
    workflow: {
        title: string
        intro: string
        blocks: { when: string; title: string; items: { tool?: ToolId; text: string }[] }[]
    }
    tools: Record<ToolId, ToolGuide>
    troubleshooting: {
        title: string
        intro: string
        sections: { title: string; items: QA[] }[]
    }
    glossary: {
        title: string
        intro: string
    }
}
